import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2, ExternalLink, Pause, Repeat, Route, Trash2, Undo2, Wrench } from 'lucide-react'
import type { Dream, DreamBoardCard } from '../../services/dreamService'
import { dreamState, shownProgress, targetPassed } from '../../utils/dreamSections'
import { nextWhenWords, nextWords } from '../../utils/dreamWords'
import { lifeAreaLabel } from '../../utils/taskForm'
import { ActionMenu } from '../ui/ActionMenu'
import { Timestamp } from '../ui/Timestamp'
import { Last14Practice } from '../skills/SkillKeepingUp'

/**
 * One row of the dream list (Dreams UX pass 2026-09-06). A progress mark you
 * can tap to finish the dream, a title, and one quiet line: the next thing
 * under it and which goal it is for, how far along, what got done lately.
 * Everything else under ⋯.
 */
interface Props {
  dream: Dream
  card: DreamBoardCard | null | undefined
  showArea?: boolean
  timeHint?: 'created' | 'updated' | null
  tz?: string | null
  onComplete: (dream: Dream) => void | Promise<void>
  onReopen: (dream: Dream) => void | Promise<void>
  onDelete: (dream: Dream) => void
  diaryMode?: boolean
}

function ProgressMark({ pct, size = 32 }: { pct: number; size?: number }) {
  const r = (size - 6) / 2
  const c = 2 * Math.PI * r
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="3" fill="none" className="stroke-soft" />
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="3" fill="none" strokeDasharray={`${(Math.max(0, Math.min(100, pct)) / 100) * c} ${c}`} strokeLinecap="round" className="stroke-brand" />
    </svg>
  )
}

export function DreamRow({ dream, card, showArea = false, timeHint = null, tz, onComplete, onReopen, onDelete, diaryMode = false }: Props) {
  const navigate = useNavigate()
  const href = `/dreams/${dream.id}`
  const state = dreamState(dream)
  const finished = state.key === 'done' || state.key === 'let_go'
  const resting = state.key === 'paused'
  const live = !finished && !resting
  const pad2 = (n: number) => String(n).padStart(2, '0')
  const todayKey = (() => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` })()
  const checkWords = card?.path?.status === 'completed' ? 'Journey complete' : targetPassed(dream, todayKey) ? 'Its date has passed' : null
  const area = dream.life_area || (dream.life_areas && dream.life_areas[0]) || null
  const pct = shownProgress(dream, card)
  const next = nextWords(card?.next)
  const nextWhen = nextWhenWords(card?.next, tz)

  const menuItems = [
    { label: 'Open', icon: <ExternalLink className="h-4 w-4" />, onSelect: () => navigate(href), testId: 'dream-row-open' },
    ...(finished ? [{ label: 'Reopen', icon: <Undo2 className="h-4 w-4" />, onSelect: () => void onReopen(dream), testId: 'dream-row-reopen' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => onDelete(dream), danger: true, testId: 'dream-row-delete' },
  ]

  const mark = live ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); void onComplete(dream) }}
      aria-label={`Mark done: ${dream.title}`}
      title="Mark done"
      data-testid="dream-row-done"
      className="group/mark relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-ok/10 lg:h-9 lg:w-9"
    >
      <span className="group-hover/mark:hidden"><ProgressMark pct={pct} /></span>
      <CheckCircle2 className="hidden h-5 w-5 text-ok group-hover/mark:block" aria-hidden="true" />
      {pct > 0 && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-app-nano font-semibold text-secondary group-hover/mark:hidden">{pct}</span>}
    </button>
  ) : (
    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center lg:h-9 lg:w-9" aria-hidden="true">
      {state.key === 'done' ? <CheckCircle2 className="h-5 w-5 text-ok" /> : state.key === 'let_go' ? <span className="text-soft">—</span> : <Pause className="h-4 w-4 text-muted" />}
    </span>
  )

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-lg border px-1.5 py-1 transition-colors sm:gap-2 sm:px-2 ${
        diaryMode ? 'border-warn bg-warn/20 hover:bg-warn/30' : 'border-soft bg-surface hover:border-brand'
      } ${finished ? 'opacity-75' : ''}`}
      onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,[role=menu],[role=dialog]')) navigate(href) }}
      data-testid="dream-row"
      data-state={state.key}
    >
      {mark}
      <div className="min-w-0 flex-1 py-0.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <Link to={href} onClick={(e) => e.stopPropagation()} className={`max-w-full truncate font-semibold hover:text-brand ${live ? 'text-primary' : 'text-muted'} ${diaryMode ? 'font-handwritten text-base sm:text-lg' : 'text-app-body'}`} title={dream.description || undefined} data-testid="dream-row-title">
            {dream.title}
          </Link>
          {state.words && <span className="hidden shrink-0 rounded bg-surface-alt px-1.5 py-0.5 text-app-micro font-medium text-secondary sm:inline" data-testid="dream-row-state">{state.words}</span>}
          {live && checkWords && <span className="shrink-0 rounded bg-brand-subtle px-1.5 py-0.5 text-app-micro font-medium text-brand" data-testid="dream-row-review">{checkWords}</span>}
          {live && card?.today && <span className="shrink-0 text-app-micro font-medium text-ok" data-testid="dream-row-today">something done today</span>}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-x-2 overflow-hidden whitespace-nowrap text-app-micro text-muted sm:gap-x-2.5" data-testid="dream-row-meta">
          {state.words && <span className="shrink-0 sm:hidden">{state.words}</span>}
          {live && next && (
            <span className="inline-flex min-w-0 items-center gap-1 truncate font-medium text-brand" title={`${next.title}${next.sub ? ` · ${next.sub}` : ''}`} data-testid="dream-row-next">
              {card?.next?.kind === 'habit' && <Repeat className="h-3 w-3 shrink-0" aria-hidden="true" />}
              {card?.next?.kind === 'skill' && <Wrench className="h-3 w-3 shrink-0" aria-hidden="true" />}
              next: {next.title}{nextWhen ? ` · ${nextWhen}` : ''}{next.sub ? ` · ${next.sub}` : ''}
            </span>
          )}
          {live && !next && card?.path && <span className="shrink-0 inline-flex items-center gap-1"><Route className="h-3 w-3" aria-hidden="true" />journey {card.path.status === 'completed' ? 'complete' : `${card.path.done} of ${card.path.total}`}</span>}
          {live && !next && !card?.path && card?.words && <span className="shrink-0">{card.words}</span>}
          {state.key === 'done' && dream.completed_at && <span className="shrink-0">done <Timestamp value={dream.completed_at} mode="datetime" /></span>}
          {live && card && card.days_active > 0 && <span className="hidden shrink-0 md:inline-flex"><Last14Practice days={card.last_14} size="sm" /></span>}
          {showArea && area && <span className="hidden shrink-0 md:inline">{lifeAreaLabel(area)}</span>}
          {timeHint && <span className="shrink-0" data-testid="dream-row-time-hint">{timeHint === 'created' ? 'added' : 'updated'} <Timestamp value={timeHint === 'created' ? (dream.created_at || dream.updated_at || '') : (dream.updated_at || dream.created_at || '')} mode="datetime" /></span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {live && !card?.path && !card?.next && (
          <Link to={href} onClick={(e) => e.stopPropagation()} className="hidden rounded-md px-2 py-1 text-app-micro font-medium text-brand hover:bg-brand-subtle lg:inline-flex" data-testid="dream-row-plan">Plan the journey</Link>
        )}
        <ActionMenu compact label={`More for ${dream.title}`} items={menuItems} testId="dream-row-more" />
      </div>
    </div>
  )
}

export default DreamRow
